import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useOffline } from '@/contexts/OfflineContext';
import { sitesApi } from '@/lib/api';
import { 
  LayoutDashboard, Building2, Receipt, BookOpen, Users, Landmark, 
  ClipboardList, Sun, Moon, LogOut, Menu, X, Plus, Upload, ChevronRight, ArrowRightLeft, UserCog,
  Wifi, WifiOff, RefreshCw, CloudOff, ChevronDown, Layers, Tag, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const navItems = [
  { to: '/app/dashboard', icon: LayoutDashboard, label: 'Dashboard', page: 'dashboard' },
  { to: '/app/sites', icon: Building2, label: 'Sites', page: 'sites' },
  { to: '/app/entries', icon: Receipt, label: 'Entries', page: 'entries' },
  { to: '/app/transfers', icon: ArrowRightLeft, label: 'Transfers', page: 'transfers' },
  { to: '/app/ledger', icon: BookOpen, label: 'Ledger', page: 'ledger' },
  { to: '/app/combined-ledger', icon: Layers, label: 'Combined Ledger', page: 'combined-ledger' },
  { to: '/app/entities', icon: Users, label: 'Entities', page: 'entities' },
  { to: '/app/sub-categories', icon: Tag, label: 'Sub-Categories', page: 'sub-categories', adminManagerOnly: true },
  { to: '/app/partners', icon: Landmark, label: 'Partners', page: 'partners' },
  { to: '/app/cash-bank', icon: Landmark, label: 'Cash & Bank', page: 'cash-bank' },
  { to: '/app/audit-log', icon: ClipboardList, label: 'Audit Log', page: 'audit-log', adminManagerOnly: true },
  { to: '/app/users', icon: UserCog, label: 'Accounts', page: 'users', adminOnly: true },
  { to: '/app/trash', icon: Trash2, label: 'Trash', page: 'trash', adminOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { isOnline, syncQueueCount, syncing, syncPendingActions, cacheReferenceData } = useOffline();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sites, setSites] = useState([]);
  const [activeSiteId, setActiveSiteId] = useState('');

  // Load sites for logo display
  useEffect(() => {
    sitesApi.list().then(r => {
      setSites(r.data);
      // Auto-select first site if none selected
      if (r.data.length > 0 && !activeSiteId) {
        const stored = localStorage.getItem('activeSiteId');
        if (stored && r.data.find(s => s._id === stored)) {
          setActiveSiteId(stored);
        } else {
          setActiveSiteId(r.data[0]._id);
        }
      }
    }).catch(() => {});
  }, []);

  // Persist active site selection
  useEffect(() => {
    if (activeSiteId) localStorage.setItem('activeSiteId', activeSiteId);
  }, [activeSiteId]);

  // Cache reference data when component mounts and user is online
  useEffect(() => {
    if (isOnline) cacheReferenceData();
  }, [isOnline, cacheReferenceData]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const activeSite = sites.find(s => s._id === activeSiteId);
  const logoPath = theme === 'dark'
    ? (activeSite?.logo_dark || activeSite?.logo_light)
    : (activeSite?.logo_light || activeSite?.logo_dark);

  return (
    <div className="flex h-screen overflow-hidden bg-background" data-testid="app-layout">
      {/* Sidebar Overlay (Mobile) */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-200 ease-in-out flex flex-col ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="border-b border-border">
          <div className="h-16 flex items-center px-5 gap-2.5" data-testid="sidebar-logo">
            {logoPath ? (
              <img src={`${API_URL}/api/files/${logoPath}`} alt={activeSite?.name || 'Logo'} className="h-8 w-auto max-w-[120px] object-contain" />
            ) : (
              <>
                <img src="/logo192.png" alt="OUR CASH FLOW" className="w-7 h-7 shrink-0 rounded" />
                <span className="text-lg font-bold tracking-tight truncate">OUR CASH FLOW</span>
              </>
            )}
          </div>
          {sites.length > 1 && (
            <div className="px-3 pb-3">
              <Select value={activeSiteId} onValueChange={setActiveSiteId}>
                <SelectTrigger className="h-8 text-xs" data-testid="active-site-selector">
                  <SelectValue placeholder="Select site" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map(s => <SelectItem key={s._id} value={s._id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
          {navItems.map(item => {
            // Admin-only pages (user management)
            if (item.adminOnly && user?.role !== 'admin') return null;
            // Admin/Manager only pages (audit log)
            if (item.adminManagerOnly && !['admin', 'manager'].includes(user?.role)) return null;
            // For partner/viewer: check allowed_pages
            if (user?.role === 'partner' || user?.role === 'viewer') {
              const allowed = user?.allowed_pages || [];
              if (item.page && !allowed.includes(item.page)) return null;
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => 
                  `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive 
                      ? 'bg-primary text-primary-foreground' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="w-4 h-4" strokeWidth={1.5} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border space-y-2">
          <div className="px-3 py-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {user?.name || user?.email}
          </div>
          <div className="text-xs px-3 py-1 text-muted-foreground/70 uppercase tracking-wider">{user?.role}</div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 bg-background border-b border-border sticky top-0 z-30 flex items-center justify-between px-4 md:px-8">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 hover:bg-muted rounded" data-testid="mobile-menu-btn">
              <Menu className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center gap-2">
            {/* Offline/Online Status */}
            {!isOnline && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 text-xs font-medium rounded-full" data-testid="offline-indicator">
                <WifiOff className="w-3 h-3" />
                <span className="hidden sm:inline">Offline</span>
              </div>
            )}
            {/* Sync Queue Badge */}
            {syncQueueCount > 0 && (
              <button
                onClick={syncPendingActions}
                disabled={syncing || !isOnline}
                className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 text-xs font-medium rounded-full hover:bg-blue-200 dark:hover:bg-blue-900/60 transition-colors disabled:opacity-50"
                title={isOnline ? 'Sync pending changes' : 'Go online to sync'}
                data-testid="sync-queue-badge"
              >
                <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
                <span>{syncQueueCount} pending</span>
              </button>
            )}
            {['admin', 'manager'].includes(user?.role) && (
              <>
                <Button variant="outline" size="sm" className="gap-1.5 hidden sm:flex" onClick={() => navigate('/app/entries?action=new')} data-testid="quick-add-entry">
                  <Plus className="w-3.5 h-3.5" /> Entry
                </Button>
                <Button variant="outline" size="sm" className="gap-1.5 hidden sm:flex" onClick={() => navigate('/app/entries?action=bulk')} data-testid="quick-bulk-upload">
                  <Upload className="w-3.5 h-3.5" /> Bulk
                </Button>
              </>
            )}
            <button onClick={toggleTheme} className="p-2 hover:bg-muted rounded transition-colors" data-testid="theme-toggle">
              {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            </button>
            <button onClick={handleLogout} className="p-2 hover:bg-muted rounded transition-colors text-muted-foreground" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </header>
        {/* Content */}
        {!isOnline && (
          <div className="mx-4 md:mx-6 lg:mx-8 mt-3 flex items-center gap-2 px-4 py-2.5 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-sm" data-testid="offline-banner">
            <CloudOff className="w-4 h-4 shrink-0" />
            <span>You're offline. Changes will be saved locally and synced when you reconnect.</span>
            {syncQueueCount > 0 && <span className="ml-auto text-xs font-mono">{syncQueueCount} queued</span>}
          </div>
        )}
        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      {/* Floating action button (mobile) */}
      {['admin', 'manager'].includes(user?.role) && (
        <button
          onClick={() => navigate('/app/entries?action=new')}
          className="fixed bottom-6 right-6 lg:hidden z-30 w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-lg flex items-center justify-center hover:opacity-90 transition-opacity"
          data-testid="fab-add-entry"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
